import React, { useState, useRef, useEffect } from 'react';
import { IonIcon, IonSpinner } from '@ionic/react';
import { closeOutline, checkmarkCircle } from 'ionicons/icons';
import { useTranslation } from '../hooks/useTranslation';
import './TokenInputModal.css';

interface Props {
  eventId: string;
  eventName: string;
  userId: string;
  onClose: () => void;
  onSuccess: () => void;
  onRedeem: (token: string, eventId: string, userId: string) => Promise<{ success: boolean; error?: string }>;
}

const TOKEN_LENGTH = 6;

const TokenInputModal: React.FC<Props> = ({
  eventId, eventName, userId, onClose, onSuccess, onRedeem
}) => {
  const { t } = useTranslation();
  const [digits, setDigits] = useState<string[]>(Array(TOKEN_LENGTH).fill(''));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index: number, value: string) => {
    const char = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(-1);
    const next = [...digits];
    next[index] = char;
    setDigits(next);
    setError('');
    if (char && index < TOKEN_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace') {
      if (digits[index]) {
        const next = [...digits];
        next[index] = '';
        setDigits(next);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text')
      .replace(/[^A-Za-z0-9]/g, '')
      .toUpperCase()
      .slice(0, TOKEN_LENGTH);
    if (!pasted) return;
    const next = Array(TOKEN_LENGTH).fill('');
    pasted.split('').forEach((c, i) => { next[i] = c; });
    setDigits(next);
    setError('');
    const focusIdx = Math.min(pasted.length, TOKEN_LENGTH - 1);
    inputRefs.current[focusIdx]?.focus();
  };

  const handleSubmit = async () => {
    const token = digits.join('');
    if (token.length < TOKEN_LENGTH) {
      setError(t('token.incomplete'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await onRedeem(token, eventId, userId);
      if (result.success) {
        setSuccess(true);
        setTimeout(() => { onSuccess(); }, 1800);
      } else {
        setError(t('token.invalid'));
      }
    } catch {
      setError(t('token.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="token-modal-overlay" onClick={!loading ? onClose : undefined}>
      <div className="token-modal" onClick={e => e.stopPropagation()}>

        {success ? (
          <div className="token-modal-success">
            <IonIcon icon={checkmarkCircle} className="token-success-icon" />
            <h2>{t('token.success')}</h2>
            <p>{t('token.success_desc')}</p>
          </div>
        ) : (
          <>
            <div className="token-modal-header">
              <div>
                <h2>{t('token.enter_code')}</h2>
                <p className="token-modal-event-name">{eventName}</p>
              </div>
              <button className="token-modal-close" onClick={onClose} disabled={loading}>
                <IonIcon icon={closeOutline} />
              </button>
            </div>

            <div className="token-modal-body">
              <p className="token-modal-hint">{t('token.hint')}</p>

              <div className="token-digits" onPaste={handlePaste}>
                {digits.map((d, i) => (
                  <input
                    key={i}
                    ref={el => { inputRefs.current[i] = el; }}
                    className={`token-digit-input ${d ? 'token-digit-input--filled' : ''} ${error ? 'token-digit-input--error' : ''}`}
                    type="text"
                    inputMode="text"
                    maxLength={1}
                    value={d}
                    onChange={e => handleChange(i, e.target.value)}
                    onKeyDown={e => handleKeyDown(i, e)}
                    disabled={loading}
                  />
                ))}
              </div>

              {error && <p className="token-error">{error}</p>}

              <button
                className="token-submit-btn"
                onClick={handleSubmit}
                disabled={loading || digits.join('').length < TOKEN_LENGTH}
              >
                {loading ? <IonSpinner name="crescent" /> : t('token.submit')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TokenInputModal;
